import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  loadBigPurchaseList,
  loadBigPurchases,
  loadDailyPurchaseList,
  loadDailyPurchases,
  loadUtilityBillList,
  loadUtilityBills,
  modifyBigPurchase,
  modifyDailyPurchase,
  modifyUtilityBillWithDocument,
  removeBigPurchase,
  removeDailyPurchase,
  removeUtilityBill,
  saveBigPurchase,
  saveDailyPurchase,
  saveUtilityBillWithDocument,
} from '../api/purchase'
import removeFile from '../../Files/api/removeFile'
import {
  BigPurchase,
  CreateBigPurchase,
  CreateDailyPurchase,
  CreateUtilityBillInput,
  DailyPurchase,
  PaginatedPurchases,
  PaginationMeta,
  PurchaseListRequest,
  UpdateBigPurchase,
  UtilityBillListRequest,
  UpdateDailyPurchase,
  UpdateUtilityBillInput,
  UtilityBill,
} from '../types/purchase'

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
}

const nextTotalPages = (total: number, limit: number) => {
  return total === 0 ? 0 : Math.ceil(total / (limit || 25))
}

const sortByPurchaseDateDesc = <
  T extends { purchaseDate: string; createdAt: string },
>(
  items: T[],
): T[] => {
  return [...items].sort((a, b) => {
    const byDate = b.purchaseDate.localeCompare(a.purchaseDate)
    if (byDate !== 0) {
      return byDate
    }
    return b.createdAt.localeCompare(a.createdAt)
  })
}

export const getDailyPurchases = createAsyncThunk(
  'purchaseList/getDailyPurchases',
  async (_, thunkApi) => {
    try {
      return await loadDailyPurchases()
    } catch (e) {
      return thunkApi.rejectWithValue('getDailyPurchasesError')
    }
  },
)

export const getDailyPurchaseList = createAsyncThunk(
  'purchaseList/getDailyPurchaseList',
  async (request: PurchaseListRequest, thunkApi) => {
    try {
      return await loadDailyPurchaseList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('getDailyPurchaseListError')
    }
  },
)

export const addDailyPurchase = createAsyncThunk(
  'purchaseList/addDailyPurchase',
  async (purchase: CreateDailyPurchase, thunkApi) => {
    try {
      return await saveDailyPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('addDailyPurchaseError')
    }
  },
)

export const updateDailyPurchase = createAsyncThunk(
  'purchaseList/updateDailyPurchase',
  async (purchase: UpdateDailyPurchase, thunkApi) => {
    try {
      return await modifyDailyPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('updateDailyPurchaseError')
    }
  },
)

export const deleteDailyPurchase = createAsyncThunk(
  'purchaseList/deleteDailyPurchase',
  async (id: string, thunkApi) => {
    try {
      return await removeDailyPurchase(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteDailyPurchaseError')
    }
  },
)

export const getBigPurchases = createAsyncThunk(
  'purchaseList/getBigPurchases',
  async (_, thunkApi) => {
    try {
      return await loadBigPurchases()
    } catch (e) {
      return thunkApi.rejectWithValue('getBigPurchasesError')
    }
  },
)

export const getBigPurchaseList = createAsyncThunk(
  'purchaseList/getBigPurchaseList',
  async (request: PurchaseListRequest, thunkApi) => {
    try {
      return await loadBigPurchaseList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('getBigPurchaseListError')
    }
  },
)

export const addBigPurchase = createAsyncThunk(
  'purchaseList/addBigPurchase',
  async (purchase: CreateBigPurchase, thunkApi) => {
    try {
      return await saveBigPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('addBigPurchaseError')
    }
  },
)

export const updateBigPurchase = createAsyncThunk(
  'purchaseList/updateBigPurchase',
  async (purchase: UpdateBigPurchase, thunkApi) => {
    try {
      return await modifyBigPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('updateBigPurchaseError')
    }
  },
)

export const deleteBigPurchase = createAsyncThunk(
  'purchaseList/deleteBigPurchase',
  async (id: string, thunkApi) => {
    try {
      return await removeBigPurchase(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteBigPurchaseError')
    }
  },
)

const sortByBillDateDesc = <
  T extends { billDate?: string | null; createdAt: string },
>(
  items: T[],
): T[] => {
  return [...items].sort((a, b) => {
    const byDate = (b.billDate || '').localeCompare(a.billDate || '')
    if (byDate !== 0) {
      return byDate
    }
    return b.createdAt.localeCompare(a.createdAt)
  })
}

export const getUtilityBills = createAsyncThunk(
  'purchaseList/getUtilityBills',
  async (_, thunkApi) => {
    try {
      return await loadUtilityBills()
    } catch (e) {
      return thunkApi.rejectWithValue('getUtilityBillsError')
    }
  },
)

export const getUtilityBillList = createAsyncThunk(
  'purchaseList/getUtilityBillList',
  async (request: UtilityBillListRequest, thunkApi) => {
    try {
      return await loadUtilityBillList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('getUtilityBillListError')
    }
  },
)

export const addUtilityBill = createAsyncThunk(
  'purchaseList/addUtilityBill',
  async (bill: CreateUtilityBillInput, thunkApi) => {
    try {
      return await saveUtilityBillWithDocument(bill)
    } catch (e) {
      return thunkApi.rejectWithValue('addUtilityBillError')
    }
  },
)

export const updateUtilityBill = createAsyncThunk(
  'purchaseList/updateUtilityBill',
  async (bill: UpdateUtilityBillInput, thunkApi) => {
    try {
      return await modifyUtilityBillWithDocument(bill)
    } catch (e) {
      return thunkApi.rejectWithValue('updateUtilityBillError')
    }
  },
)

export const deleteUtilityBill = createAsyncThunk(
  'purchaseList/deleteUtilityBill',
  async (id: string, thunkApi) => {
    try {
      const state = thunkApi.getState() as {
        purchaseList?: { utilityBills?: UtilityBill[] }
      }
      const existing = state.purchaseList?.utilityBills?.find(
        (bill) => bill.id === id,
      )
      const result = await removeUtilityBill(id)
      const fileIds = Array.from(
        new Set(
          [
            ...(existing?.documentFiles || []).map((file) => file.id),
            existing?.documentFileId || '',
          ].filter(Boolean),
        ),
      )

      await Promise.all(
        fileIds.map((fileId) => removeFile(fileId).catch(() => undefined)),
      )

      return result
    } catch (e) {
      return thunkApi.rejectWithValue('deleteUtilityBillError')
    }
  },
)

const sortByCreatedAtDesc = <T extends { createdAt: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

interface PurchaseSliceState {
  dailyPurchases: DailyPurchase[]
  pagedDailyPurchases: DailyPurchase[]
  dailyPagination: PaginationMeta
  bigPurchases: BigPurchase[]
  pagedBigPurchases: BigPurchase[]
  bigPagination: PaginationMeta
  utilityBills: UtilityBill[]
  pagedUtilityBills: UtilityBill[]
  utilityPagination: PaginationMeta
  utilityListCategory?: string
  dailyLoading: boolean
  dailyListLoading: boolean
  bigLoading: boolean
  bigListLoading: boolean
  utilityLoading: boolean
  utilityListLoading: boolean
  dailyListRequestId?: string
  bigListRequestId?: string
  utilityListRequestId?: string
  error?: SerializedError | string
}

const initialState: PurchaseSliceState = {
  dailyPurchases: [],
  pagedDailyPurchases: [],
  dailyPagination: emptyPagination,
  bigPurchases: [],
  pagedBigPurchases: [],
  bigPagination: emptyPagination,
  utilityBills: [],
  pagedUtilityBills: [],
  utilityPagination: emptyPagination,
  utilityListCategory: undefined,
  dailyLoading: false,
  dailyListLoading: false,
  bigLoading: false,
  bigListLoading: false,
  utilityLoading: false,
  utilityListLoading: false,
  dailyListRequestId: undefined,
  bigListRequestId: undefined,
  utilityListRequestId: undefined,
  error: undefined,
}

const purchaseListSlice = createSlice({
  name: 'purchaseList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getDailyPurchases.fulfilled, (
      state,
      action: PayloadAction<DailyPurchase[]>,
    ) => {
      state.dailyPurchases = sortByPurchaseDateDesc(action.payload)
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(getDailyPurchases.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(getDailyPurchases.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(getDailyPurchaseList.fulfilled, (
      state,
      action: PayloadAction<PaginatedPurchases<DailyPurchase>> & { meta: any },
    ) => {
      if (state.dailyListRequestId !== action.meta.requestId) {
        return
      }

      state.pagedDailyPurchases = sortByPurchaseDateDesc(action.payload.items)
      state.dailyPagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.dailyListRequestId = undefined
      state.dailyListLoading = false
      state.error = undefined
    })
    builder.addCase(getDailyPurchaseList.pending, (state, action: any) => {
      state.dailyListRequestId = action.meta.requestId
      state.dailyListLoading = true
      state.error = undefined
    })
    builder.addCase(getDailyPurchaseList.rejected, (state, action: any) => {
      if (state.dailyListRequestId !== action.meta.requestId) {
        return
      }

      state.dailyListRequestId = undefined
      state.dailyListLoading = false
      state.error = action.error
    })
    builder.addCase(addDailyPurchase.fulfilled, (
      state,
      action: PayloadAction<DailyPurchase>,
    ) => {
      state.dailyPurchases = sortByPurchaseDateDesc([
        action.payload,
        ...state.dailyPurchases,
      ])
      const limit = state.dailyPagination.limit || 25
      if (state.dailyPagination.page === 1) {
        state.pagedDailyPurchases = sortByPurchaseDateDesc([
          action.payload,
          ...state.pagedDailyPurchases.filter(
            (purchase) => purchase.id !== action.payload.id,
          ),
        ]).slice(0, limit)
      }
      const total = state.dailyPagination.total + 1
      state.dailyPagination.total = total
      state.dailyPagination.totalPages = nextTotalPages(total, limit)
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(addDailyPurchase.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(addDailyPurchase.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(updateDailyPurchase.fulfilled, (
      state,
      action: PayloadAction<DailyPurchase>,
    ) => {
      state.dailyPurchases = sortByPurchaseDateDesc(
        state.dailyPurchases.map((purchase) =>
          purchase.id === action.payload.id ? action.payload : purchase,
        ),
      )
      state.pagedDailyPurchases = sortByPurchaseDateDesc(
        state.pagedDailyPurchases.map((purchase) =>
          purchase.id === action.payload.id ? action.payload : purchase,
        ),
      )
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(updateDailyPurchase.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(updateDailyPurchase.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(deleteDailyPurchase.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.dailyPurchases = state.dailyPurchases.filter(
        (purchase) => purchase.id !== action.payload,
      )
      state.pagedDailyPurchases = state.pagedDailyPurchases.filter(
        (purchase) => purchase.id !== action.payload,
      )
      const dailyTotal = Math.max(0, state.dailyPagination.total - 1)
      state.dailyPagination.total = dailyTotal
      state.dailyPagination.totalPages = nextTotalPages(
        dailyTotal,
        state.dailyPagination.limit || 25,
      )
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(deleteDailyPurchase.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(deleteDailyPurchase.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(getBigPurchases.fulfilled, (
      state,
      action: PayloadAction<BigPurchase[]>,
    ) => {
      state.bigPurchases = sortByPurchaseDateDesc(action.payload)
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(getBigPurchases.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(getBigPurchases.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(getBigPurchaseList.fulfilled, (
      state,
      action: PayloadAction<PaginatedPurchases<BigPurchase>> & { meta: any },
    ) => {
      if (state.bigListRequestId !== action.meta.requestId) {
        return
      }

      state.pagedBigPurchases = sortByPurchaseDateDesc(action.payload.items)
      state.bigPagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.bigListRequestId = undefined
      state.bigListLoading = false
      state.error = undefined
    })
    builder.addCase(getBigPurchaseList.pending, (state, action: any) => {
      state.bigListRequestId = action.meta.requestId
      state.bigListLoading = true
      state.error = undefined
    })
    builder.addCase(getBigPurchaseList.rejected, (state, action: any) => {
      if (state.bigListRequestId !== action.meta.requestId) {
        return
      }

      state.bigListRequestId = undefined
      state.bigListLoading = false
      state.error = action.error
    })
    builder.addCase(addBigPurchase.fulfilled, (
      state,
      action: PayloadAction<BigPurchase>,
    ) => {
      state.bigPurchases = sortByPurchaseDateDesc([
        action.payload,
        ...state.bigPurchases,
      ])
      const limit = state.bigPagination.limit || 25
      if (state.bigPagination.page === 1) {
        state.pagedBigPurchases = sortByPurchaseDateDesc([
          action.payload,
          ...state.pagedBigPurchases.filter(
            (purchase) => purchase.id !== action.payload.id,
          ),
        ]).slice(0, limit)
      }
      const total = state.bigPagination.total + 1
      state.bigPagination.total = total
      state.bigPagination.totalPages = nextTotalPages(total, limit)
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(addBigPurchase.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(addBigPurchase.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(updateBigPurchase.fulfilled, (
      state,
      action: PayloadAction<BigPurchase>,
    ) => {
      state.bigPurchases = sortByPurchaseDateDesc(
        state.bigPurchases.map((purchase) =>
          purchase.id === action.payload.id ? action.payload : purchase,
        ),
      )
      state.pagedBigPurchases = sortByPurchaseDateDesc(
        state.pagedBigPurchases.map((purchase) =>
          purchase.id === action.payload.id ? action.payload : purchase,
        ),
      )
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(updateBigPurchase.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(updateBigPurchase.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(deleteBigPurchase.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.bigPurchases = state.bigPurchases.filter(
        (purchase) => purchase.id !== action.payload,
      )
      state.pagedBigPurchases = state.pagedBigPurchases.filter(
        (purchase) => purchase.id !== action.payload,
      )
      const bigTotal = Math.max(0, state.bigPagination.total - 1)
      state.bigPagination.total = bigTotal
      state.bigPagination.totalPages = nextTotalPages(
        bigTotal,
        state.bigPagination.limit || 25,
      )
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(deleteBigPurchase.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(deleteBigPurchase.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(getUtilityBills.fulfilled, (
      state,
      action: PayloadAction<UtilityBill[]>,
    ) => {
      state.utilityBills = sortByCreatedAtDesc(action.payload)
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(getUtilityBills.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(getUtilityBills.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
    builder.addCase(getUtilityBillList.fulfilled, (
      state,
      action: PayloadAction<PaginatedPurchases<UtilityBill>> & { meta: any },
    ) => {
      if (state.utilityListRequestId !== action.meta.requestId) {
        return
      }

      state.pagedUtilityBills = action.payload.items
      state.utilityPagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.utilityListCategory = action.meta.arg?.category
      state.utilityListRequestId = undefined
      state.utilityListLoading = false
      state.error = undefined
    })
    builder.addCase(getUtilityBillList.pending, (state, action: any) => {
      state.utilityListRequestId = action.meta.requestId
      state.utilityListLoading = true
      state.error = undefined
    })
    builder.addCase(getUtilityBillList.rejected, (state, action: any) => {
      if (state.utilityListRequestId !== action.meta.requestId) {
        return
      }

      state.utilityListRequestId = undefined
      state.utilityListLoading = false
      state.error = action.error
    })
    builder.addCase(addUtilityBill.fulfilled, (
      state,
      action: PayloadAction<UtilityBill>,
    ) => {
      state.utilityBills = sortByCreatedAtDesc([
        action.payload,
        ...state.utilityBills,
      ])
      if (action.payload.category === state.utilityListCategory) {
        const limit = state.utilityPagination.limit || 25
        if (state.utilityPagination.page === 1) {
          state.pagedUtilityBills = sortByBillDateDesc([
            action.payload,
            ...state.pagedUtilityBills.filter(
              (bill) => bill.id !== action.payload.id,
            ),
          ]).slice(0, limit)
        }
        const total = state.utilityPagination.total + 1
        state.utilityPagination.total = total
        state.utilityPagination.totalPages = nextTotalPages(total, limit)
      }
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(addUtilityBill.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(addUtilityBill.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
    builder.addCase(updateUtilityBill.fulfilled, (
      state,
      action: PayloadAction<UtilityBill>,
    ) => {
      state.utilityBills = sortByCreatedAtDesc(
        state.utilityBills.map((bill) =>
          bill.id === action.payload.id ? action.payload : bill,
        ),
      )
      state.pagedUtilityBills = sortByBillDateDesc(
        state.pagedUtilityBills.map((bill) =>
          bill.id === action.payload.id ? action.payload : bill,
        ),
      )
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(updateUtilityBill.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(updateUtilityBill.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
    builder.addCase(deleteUtilityBill.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.utilityBills = state.utilityBills.filter(
        (bill) => bill.id !== action.payload,
      )
      const removedPagedBill = state.pagedUtilityBills.some(
        (bill) => bill.id === action.payload,
      )
      state.pagedUtilityBills = state.pagedUtilityBills.filter(
        (bill) => bill.id !== action.payload,
      )
      if (removedPagedBill) {
        const utilityTotal = Math.max(0, state.utilityPagination.total - 1)
        state.utilityPagination.total = utilityTotal
        state.utilityPagination.totalPages = nextTotalPages(
          utilityTotal,
          state.utilityPagination.limit || 25,
        )
      }
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(deleteUtilityBill.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(deleteUtilityBill.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
  },
})

export default purchaseListSlice.reducer
